import { Store } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";

import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import store from "./redux/store";
import { Auth0Provider } from "./shared/Auth0Utility"; // custom auth0 provider
import "./styles/tailwind.css";

// Extend the Window interface in the global namespace
// Taken from https://stackoverflow.com/questions/56457935/typescript-error-property-x-does-not-exist-on-type-window
declare global {
  interface Window {
    store: Store;
    Cypress: any;
  }
}

// If we are running Cypress tests, set the store equal to the window.store so it's accessible
if (window.Cypress) {
  window.store = store;
}

const urls =
  import.meta.env.VITE_RUN_ENV === "env-stage"
    ? ["https://env-stage-api.rebootmotion.com/"]
    : ["https://api.rebootmotion.com/"];

Sentry.init({
  dsn: "https://33a062c936f64b2d9ecaaf625a277e4c@o867858.ingest.sentry.io/5823920",
  environment: import.meta.env.VITE_RUN_ENV,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      networkDetailAllowUrls: urls,
    }),
  ],
  tracesSampleRate: import.meta.env.MODE === "test" ? 1.0 : 0.6,
  enabled: !import.meta.env.DEV,
});

const persistor = persistStore(store);

const container = document.getElementById("root");

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || "";
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || "";
const apiIdentifier = import.meta.env.VITE_AUTH0_AUDIENCE || "";

root.render(
  <Auth0Provider
    domain={auth0Domain}
    clientId={auth0ClientId}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: apiIdentifier,
    }}
  >
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorBoundary>
          <Router>
            <Toaster containerStyle={{ zIndex: 999999 }} />
            <Routes>
              <Route path={"/*"} element={<App />} />
            </Routes>
          </Router>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  </Auth0Provider>,
);
